import React, { Component } from 'react'

/**
 * Barra vertical de cine por viewport (estilo OHIF / Stone).
 * Arriba = primer corte; abajo = último. Drag o click en el track.
 */
class ViewportCineScrollbar extends Component {
  state = {
    index: 0,
    max: 1,
    dragging: false,
  }

  trackRef = React.createRef()
  _raf = null
  _pending = null
  _bound = false
  _dragging = false
  _pointerId = null

  setSlice = (index, max) => {
    const nextMax = Math.max(1, Number(max) || 1)
    const nextIndex = Math.max(0, Math.min(nextMax - 1, Number(index) || 0))
    if (this.state.index === nextIndex && this.state.max === nextMax) return
    this.setState({ index: nextIndex, max: nextMax })
  }

  componentDidMount() {
    const el = this.trackRef.current
    if (!el) return
    el.addEventListener('wheel', this.onWheel, { passive: false })
    this._bound = true
  }

  componentWillUnmount() {
    if (this._raf) {
      cancelAnimationFrame(this._raf)
      this._raf = null
    }
    const el = this.trackRef.current
    if (el && this._bound) {
      el.removeEventListener('wheel', this.onWheel)
    }
    this.unbindWindowPointer()
    this.releasePointer()
  }

  bindWindowPointer = () => {
    if (this._winBound) return
    window.addEventListener('pointermove', this.onPointerMove, { passive: false })
    window.addEventListener('pointerup', this.onPointerUp, { passive: false })
    window.addEventListener('pointercancel', this.onPointerUp, { passive: false })
    this._winBound = true
  }

  unbindWindowPointer = () => {
    if (!this._winBound) return
    window.removeEventListener('pointermove', this.onPointerMove)
    window.removeEventListener('pointerup', this.onPointerUp)
    window.removeEventListener('pointercancel', this.onPointerUp)
    this._winBound = false
  }

  releasePointer = () => {
    const el = this.trackRef.current
    if (el && el.hasPointerCapture && this._pointerId != null) {
      try {
        if (el.hasPointerCapture(this._pointerId)) el.releasePointerCapture(this._pointerId)
      } catch (e) {
        /* ignore */
      }
    }
    this._pointerId = null
  }

  indexFromClientY = (clientY) => {
    const el = this.trackRef.current
    const max = this.state.max
    if (!el || max <= 1) return 0
    const rect = el.getBoundingClientRect()
    if (!(rect.height > 0)) return this.state.index
    const t = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height))
    return Math.round(t * (max - 1))
  }

  emitSeek = (index) => {
    this._pending = index
    if (this._raf) return
    this._raf = requestAnimationFrame(() => {
      this._raf = null
      const next = this._pending
      if (next == null) return
      if (typeof this.props.onSeek === 'function') this.props.onSeek(next)
    })
  }

  applyIndex = (index, { immediate } = {}) => {
    const max = this.state.max
    const next = Math.max(0, Math.min(max - 1, index))
    if (next === this.state.index) return
    this.setState({ index: next })
    if (immediate) {
      if (typeof this.props.onSeek === 'function') this.props.onSeek(next)
      return
    }
    this.emitSeek(next)
  }

  stopEvt = (e) => {
    try {
      e.preventDefault()
      e.stopPropagation()
    } catch (err) {
      /* ignore */
    }
  }

  onPointerDown = (e) => {
    if (e.button != null && e.button !== 0) return
    this.stopEvt(e)
    if (typeof this.props.onActivate === 'function') this.props.onActivate()
    // Flag síncrono: setState es async y los primeros pointermove se perdían
    this._dragging = true
    this.setState({ dragging: true })
    this._pointerId = e.pointerId
    const el = this.trackRef.current
    if (el && e.pointerId != null && el.setPointerCapture) {
      try {
        el.setPointerCapture(e.pointerId)
      } catch (err) {
        /* ignore */
      }
    }
    this.bindWindowPointer()
    this.applyIndex(this.indexFromClientY(e.clientY), { immediate: true })
  }

  onPointerMove = (e) => {
    if (!this._dragging) return
    this.stopEvt(e)
    this.applyIndex(this.indexFromClientY(e.clientY))
  }

  onPointerUp = (e) => {
    if (!this._dragging) return
    this.stopEvt(e)
    this._dragging = false
    this.setState({ dragging: false })
    this.unbindWindowPointer()
    this.releasePointer()
  }

  onWheel = (e) => {
    if (this.state.max <= 1) return
    this.stopEvt(e)
    if (typeof this.props.onActivate === 'function') this.props.onActivate()
    const mag = Math.abs(e.deltaY)
    // Rueda discreta: ~2 cortes; trackpad fino: 1 corte (un poco más rápido que antes).
    const steps =
      mag >= 40 ? Math.max(2, Math.min(5, Math.round(mag / 55))) : 1
    const dir = e.deltaY > 0 ? 1 : -1
    this.applyIndex(this.state.index + dir * steps, { immediate: true })
  }

  render() {
    const { index, max, dragging } = this.state
    if (max <= 1) return null
    const ratio = max <= 1 ? 0 : index / (max - 1)
    const thumbTop = `${ratio * 100}%`

    return (
      <div
        className={`udv-cine-scroll${dragging ? ' is-dragging' : ''}`}
        onMouseDown={this.stopEvt}
        onTouchStart={this.stopEvt}
      >
        <div
          ref={this.trackRef}
          className="udv-cine-scroll-track"
          role="slider"
          tabIndex={0}
          aria-label="Navegar cortes"
          aria-orientation="vertical"
          aria-valuemin={1}
          aria-valuemax={max}
          aria-valuenow={index + 1}
          onPointerDown={this.onPointerDown}
          onPointerMove={this.onPointerMove}
          onPointerUp={this.onPointerUp}
          onPointerCancel={this.onPointerUp}
        >
          <div className="udv-cine-scroll-rail" />
          <div
            className="udv-cine-scroll-thumb"
            style={{ top: thumbTop }}
          />
        </div>
        {dragging ? (
          <div className="udv-cine-scroll-tip" style={{ top: thumbTop }}>
            {index + 1}/{max}
          </div>
        ) : null}
      </div>
    )
  }
}

export default ViewportCineScrollbar
